// Push-Mitteilungen auf iPhone/iPad (APNs) und Android (FCM) – Begriffe, Kategorien und Texte für Server und Oberfläche
// (docs/mobile/SPEZIFIKATION.md §12). Jede In-App-Benachrichtigung an eine Person geht zusätzlich als Push-Mitteilung an
// ihre Geräte, gefiltert nach ihren Einstellungen. Der Sperrbildschirm zeigt nie den Text der Benachrichtigung (darin
// stehen Namen von Kunden und Lieferanten, Beträge, Gründe), sondern nur einen neutralen Titel und Satz je Art.
//
//   pushCategoryOf(kind)             Art der Benachrichtigung → Kategorie (Freigaben, Aufgaben, Meldebestand, MHD, Weitere)
//   pushContentOf(kind, severity)    neutraler Titel und Satz für den Sperrbildschirm
//   pushPathOf(link)                 Ziel in der App: nur Pfade auf dem eigenen Server ('/…'), sonst '/'
import { z } from 'zod'

export const PUSH_PLATFORMS = ['ios', 'android'] as const
export type PushPlatform = (typeof PUSH_PLATFORMS)[number]

/** APNs-Umgebung des Tokens: Debug-Builds der App melden 'sandbox', Store- und TestFlight-Builds 'production'. Android immer 'production'. */
export const PUSH_ENVIRONMENTS = ['sandbox', 'production'] as const
export type PushEnvironment = (typeof PUSH_ENVIRONMENTS)[number]

export const PUSH_CATEGORIES = ['approvals', 'tasks', 'stock', 'expiry', 'system'] as const
export type PushCategory = (typeof PUSH_CATEGORIES)[number]

export interface PushCategoryInfo {
  title: string
  description: string
  /** Vorgabe, solange die Person nichts eingestellt hat */
  defaultEnabled: boolean
}

/**
 * Kategorien in der Reihenfolge des Profils. Vorgabe: alles an – eingeschaltet hat die Person Push ohnehin selbst.
 * „MHD“ zeigt das Profil nur Betrieben mit der Funktion `lots` (Chargen und MHD, PUSH_CATEGORY_FEATURE).
 */
export const PUSH_CATEGORY_INFO: Record<PushCategory, PushCategoryInfo> = {
  approvals: {
    title: 'Freigaben',
    description: 'Wenn ein Vorgang, den Sie eingereicht haben, freigegeben oder zurückgegeben wurde.',
    defaultEnabled: true,
  },
  tasks: {
    title: 'Aufgaben',
    description: 'Zählungen, Nachzählungen, eingegangene E-Rechnungen und andere Arbeit für Sie.',
    defaultEnabled: true,
  },
  stock: {
    title: 'Meldebestand',
    description: 'Anforderungen aus den Filialen und Hinweise, wenn ein Artikel knapp wird.',
    defaultEnabled: true,
  },
  expiry: {
    title: 'MHD',
    description: 'Chargen, deren Mindesthaltbarkeit bald endet oder abgelaufen ist.',
    defaultEnabled: true,
  },
  system: {
    title: 'Weitere Hinweise',
    description: 'Betrieb und System: Störungen, Kassenausfall, nicht zugestellte E-Mails.',
    defaultEnabled: true,
  },
}

/** Kategorien, die nur mit einer Branchenfunktion Sinn ergeben */
export const PUSH_CATEGORY_FEATURE: Partial<Record<PushCategory, string>> = { expiry: 'lots' }

/** Art der Benachrichtigung (`notification.kind`) → Kategorie. Unbekannte Arten von Modulen zählen als Aufgabe. */
export function pushCategoryOf(kind: string): PushCategory {
  const k = kind.trim().toLowerCase()
  if (/^lot\./.test(k) || /(^|\.)(expiry|expiring|expired|mhd)(\.|$)/.test(k)) return 'expiry'
  if (
    /^purchasing\.requisition(\.|$)/.test(k) ||
    /^stock\./.test(k) ||
    /(^|\.)(reorder|minstock|lowstock)(\.|$)/.test(k)
  )
    return 'stock'
  if (/\.(approved|returned|decided|creditreleased|approve|approval)$/.test(k) || k === 'stocktaking.review.done')
    return 'approvals'
  if (/^(kernel|mail|pos|assistant)\./.test(k) || /^sales\.here\./.test(k)) return 'system'
  return 'tasks'
}

export interface PushContent {
  title: string
  body: string
}

/** Bekannte Arten mit eigenem, neutralem Text (ohne Namen, Beträge, Gründe) */
const KIND_CONTENT: Record<string, PushContent> = {
  'stocktaking.count': {
    title: 'Zählung gestartet',
    body: 'Bitte zählen Sie mit. Tippen Sie, um die Zählliste zu öffnen.',
  },
  'stocktaking.recount': { title: 'Nachzählung erbeten', body: 'Sie werden um eine Nachzählung gebeten.' },
  'stocktaking.review.done': {
    title: 'Inventur freigegeben',
    body: 'Eine Zählung, die Sie gestartet haben, wurde freigegeben.',
  },
  'purchasing.requisition': { title: 'Neue Anforderung', body: 'Eine Filiale fordert Ware beim Einkauf an.' },
  'purchasing.requisition.dismissed': {
    title: 'Anforderung abgelehnt',
    body: 'Der Einkauf hat eine Ihrer Anforderungen abgelehnt.',
  },
  'transfer.status': { title: 'Umlagerung', body: 'Eine Umlagerung hat einen neuen Stand.' },
  'einvoice.inbound.received': {
    title: 'Neue E-Rechnung',
    body: 'Eine E-Rechnung ist eingegangen und wartet auf Prüfung.',
  },
  'platform.account.deletion': {
    title: 'Löschung beantragt',
    body: 'Eine Person bittet um die Löschung ihres Kontos.',
  },
  'pricing.campaign.result': {
    title: 'Werbe-Mitteilung versendet',
    body: 'Das Ergebnis einer Werbe-Mitteilung liegt vor.',
  },
  'mail.failed': { title: 'E-Mail nicht zugestellt', body: 'Eine E-Mail ließ sich nicht zustellen.' },
  'kernel.ops.alert': { title: 'Hinweis der Überwachung', body: 'Die Überwachung des Servers meldet etwas.' },
  'kernel.ledger.violation': {
    title: 'Bestandsprüfung',
    body: 'Die nächtliche Bestandsprüfung hat Abweichungen gefunden.',
  },
  'pos.fiscal.outage': {
    title: 'Ausfall der TSE',
    body: 'Eine Kasse meldet einen Ausfall der Sicherheitseinrichtung.',
  },
  'assistant.budget': { title: 'KI-Assistent', body: 'Das Monatsbudget des KI-Assistenten ist fast verbraucht.' },
  'sales.here.quota': {
    title: 'Fahrzeiten mit Verkehr',
    body: 'Das Freikontingent für Fahrzeiten mit Verkehr ist aufgebraucht.',
  },
}

const CATEGORY_CONTENT: Record<PushCategory, PushContent> = {
  approvals: { title: 'Freigabe', body: 'Über einen Vorgang wurde entschieden.' },
  tasks: { title: 'Neue Aufgabe', body: 'Es gibt etwas für Sie zu tun.' },
  stock: { title: 'Meldebestand', body: 'Ein Artikel hat den Meldebestand erreicht.' },
  expiry: { title: 'MHD', body: 'Chargen laufen bald ab oder sind abgelaufen.' },
  system: { title: 'Hinweis', body: 'Es gibt eine neue Meldung.' },
}

/**
 * Titel und Satz für den Sperrbildschirm – nie der Text der Benachrichtigung selbst. Unbekannte Arten bekommen den
 * Text ihrer Kategorie; „dringend“ (severity 'danger') steht vorn im Titel.
 */
export function pushContentOf(kind: string, severity: string = 'info'): PushContent {
  const k = kind.trim()
  const category = pushCategoryOf(k)
  let content = KIND_CONTENT[k]
  if (!content && category === 'approvals') {
    const lower = k.toLowerCase()
    if (/\.(approved|creditreleased)$/.test(lower))
      content = { title: 'Freigegeben', body: 'Ein Vorgang, den Sie eingereicht haben, wurde freigegeben.' }
    else if (lower.endsWith('.returned'))
      content = { title: 'Zurückgegeben', body: 'Ein Vorgang wurde Ihnen zurückgegeben.' }
    else if (lower.endsWith('.decided'))
      content = { title: 'Entschieden', body: 'Über Ihren Vorschlag wurde entschieden.' }
    else if (/\.(approve|approval)$/.test(lower))
      content = { title: 'Freigabe erbeten', body: 'Ein Vorgang wartet auf Ihre Freigabe.' }
  }
  if (!content && category === 'expiry') {
    if (/expired$/i.test(k))
      content = { title: 'MHD abgelaufen', body: 'Eine Charge ist abgelaufen und muss aussortiert werden.' }
    else if (/recall$/i.test(k)) content = { title: 'Rückruf', body: 'Für eine Charge gibt es einen Rückruf.' }
  }
  const chosen = content ?? CATEGORY_CONTENT[category]
  return severity === 'danger' ? { title: `Dringend: ${chosen.title}`, body: chosen.body } : { ...chosen }
}

/** Ziel in der App: nur ein Pfad auf dem eigenen Server ('/…', nicht '//…', ohne Steuerzeichen, höchstens 500 Zeichen) */
export function isAppPath(path: unknown): path is string {
  return (
    typeof path === 'string' && path.length <= 500 && /^\/(?!\/)/.test(path) && !/[\s\\\u0000-\u001f\u007f]/.test(path)
  )
}

/** Ziel der Mitteilung aus dem Link der Benachrichtigung; ohne gültigen Link die Startseite */
export const pushPathOf = (link: string | null | undefined): string => (isAppPath(link) ? link : '/')

// ---- Form der Gerätetoken --------------------------------------------------------------------------------------

/** APNs: Hex-Zeichen (heute 64; Apple sagt, die Länge kann sich ändern) – die App meldet Kleinbuchstaben */
export const APNS_TOKEN_PATTERN = /^[0-9a-f]{64,200}$/
/** FCM: Registrierungstoken aus Buchstaben, Ziffern und : _ - (meist um 160 Zeichen) */
export const FCM_TOKEN_PATTERN = /^[A-Za-z0-9_:-]{32,4096}$/

export function pushTokenOk(platform: PushPlatform, token: string): boolean {
  return platform === 'ios' ? APNS_TOKEN_PATTERN.test(token) : FCM_TOKEN_PATTERN.test(token)
}

/** Token in Verträgen (Form je Plattform prüft der Kern) */
export const zPushToken = z
  .string()
  .trim()
  .min(32, { error: 'Das Gerätetoken ist zu kurz.' })
  .max(4096, { error: 'Das Gerätetoken ist zu lang.' })

// ---- Texte der Oberfläche ----------------------------------------------------------------------------------------

export const PUSH_TEXTS = {
  cardTitle: 'Mitteilungen auf diesem Gerät',
  cardDescription:
    'Freigaben, Aufgaben, Meldebestand und andere Hinweise als Mitteilung auf diesem Gerät – auch wenn die App geschlossen ist. Auf dem Sperrbildschirm stehen keine Namen und Beträge.',
  switchLabel: 'Mitteilungen auf diesem Gerät',
  switchOn: 'Mitteilungen eingeschaltet',
  switchOff: 'Mitteilungen ausgeschaltet',
  denied:
    'Mitteilungen sind in den Einstellungen des Geräts für diese App ausgeschaltet. Schalten Sie sie dort ein, danach hier.',
  openSettings: 'Einstellungen öffnen',
  notConfigured: 'Auf diesem Server sind Push-Mitteilungen noch nicht eingerichtet.',
  unavailable: 'Diese App-Fassung kann keine Push-Mitteilungen empfangen.',
  updateApp: 'Bitte aktualisieren Sie die App, um Mitteilungen zu nutzen.',
  categoriesTitle: 'Was soll kommen?',
  technical: 'Technische Zugänge erhalten keine Mitteilungen.',
  noSession: 'Mitteilungen lassen sich nur in einer angemeldeten Sitzung der App einschalten.',
  badToken: 'Das Gerätetoken hat nicht die erwartete Form.',
} as const
